/**
 * AudiencePreview: the resolved list of people a send would reach, with each
 * person's outcome and a tick to leave someone out (plan
 * docs/plans/lms-plan.md 8.2 step 2 "the wizard shows the resolved list with
 * a count and lets the person untick names", 18.16; WP-43).
 *
 * Props (stable):
 *   preview            CampaignPreview | null   from useCampaignPreview (null: nothing asked yet)
 *   loading            boolean
 *   error              string | null
 *   onRetry?           () => void
 *   excluded           string[]                 the unticked user ids (sent as exclude_user_ids)
 *   onExcludedChange   (ids: string[]) => void
 *
 * The count reads "N of M people"; an unticked person stays listed (so they
 * can be ticked again) and is left out of N. Each tick carries
 * data-testid="recipient-toggle-<id>"; the count is data-testid="audience-count".
 */
import { Loader2 } from 'lucide-react';
import type { CampaignOutcome, CampaignPreview } from '../../api/campaigns';
import { CAMPAIGN_OUTCOMES } from '../../api/campaigns';
import {
  checkboxCls,
  ErrorNote,
  metaCls,
  OUTCOME_LABELS,
  OutcomePill,
  peopleCount,
  personName,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from './shared';

export interface AudiencePreviewProps {
  preview: CampaignPreview | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  excluded: string[];
  onExcludedChange: (ids: string[]) => void;
}

/** The people still ticked, and the tally of their outcomes. */
export function includedRecipients(preview: CampaignPreview | null, excluded: string[]) {
  const skip = new Set(excluded);
  const people = (preview?.recipients ?? []).filter((r) => !skip.has(r.user_id));
  const tally = CAMPAIGN_OUTCOMES.reduce(
    (acc, o) => ({ ...acc, [o]: 0 }),
    {} as Record<CampaignOutcome, number>
  );
  for (const r of people) tally[r.outcome_if_sent] = (tally[r.outcome_if_sent] ?? 0) + 1;
  return { people, tally };
}

export function AudiencePreview({ preview, loading, error, onRetry, excluded, onExcludedChange }: AudiencePreviewProps) {
  if (error) {
    return (
      <ErrorNote testId="audience-preview-error">
        {error}
        {onRetry && (
          <>
            {' '}
            <button type="button" className="font-medium underline" onClick={onRetry}>
              Try again
            </button>
          </>
        )}
      </ErrorNote>
    );
  }
  if (!preview) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="audience-preview-empty">
        {loading ? "Working out who this reaches..." : "Choose sites, roles, groups or people to see who this reaches."}
      </p>
    );
  }

  const total = preview.recipients.length;
  const { people, tally } = includedRecipients(preview, excluded);
  const toggle = (id: string) => {
    onExcludedChange(excluded.includes(id) ? excluded.filter((x) => x !== id) : [...excluded, id]);
  };

  return (
    <div className="space-y-2" data-testid="audience-preview">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-live="polite">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-testid="audience-count">
          {people.length === total ? peopleCount(total) : `${people.length.toLocaleString()} of ${peopleCount(total)}`}
        </p>
        {loading && <Loader2 aria-label="Updating" className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />}
        <p className={metaCls}>
          {CAMPAIGN_OUTCOMES.filter((o) => tally[o] > 0)
            .map((o) => `${OUTCOME_LABELS[o]}: ${tally[o].toLocaleString()}`)
            .join(', ')}
        </p>
      </div>
      {total === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">This audience reaches nobody at the sites you reach.</p>
      ) : (
        <div className={`${tableCardCls} max-h-80 overflow-y-auto`}>
          <table className={tableCls}>
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={`${thCls} w-10`}>
                  <span className="sr-only">Include</span>
                </th>
                <th scope="col" className={thCls}>
                  Person
                </th>
                <th scope="col" className={thCls}>
                  If sent
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {preview.recipients.map((r) => {
                const on = !excluded.includes(r.user_id);
                const name = personName(r);
                return (
                  <tr key={r.user_id} className={on ? '' : 'opacity-60'}>
                    <td className={tdCls}>
                      <input
                        type="checkbox"
                        className={checkboxCls}
                        checked={on}
                        onChange={() => toggle(r.user_id)}
                        aria-label={on ? `Leave out ${name}` : `Include ${name}`}
                        data-testid={`recipient-toggle-${r.user_id}`}
                      />
                    </td>
                    <td className={tdCls}>
                      <span className="block font-medium">{name}</span>
                      {r.email && <span className={metaCls}>{r.email}</span>}
                    </td>
                    <td className={tdCls}>
                      {on ? <OutcomePill outcome={r.outcome_if_sent} /> : <span className={metaCls}>Left out</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AudiencePreview;
